// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import {
    call,
    put,
    select,
} from 'redux-saga/effects';

import CustomerDal, { CreateCustomerUserRequest } from './../../../dal/CustomerDal';
import DeviceDal from './../../../dal/DeviceDal';
import RegisterDeviceDal from './../../../dal/RegisterDeviceDal';
import { getUserTierConfig, checkIsUserEnterpriseCategoryFromTier, getTierName } from './../../../lib/userTierConfig';
import {
    actionStatusStarted,
    actionStatusFailed,
    actionStatusSuccess,
    actionStatusSelector,
    LoadingState,
} from './../../../store/actionStatus';
import {
    countriesListSelector,
} from './../../../store/countries';
import {
    customerServiceLocationAddressSelector,
    getJarvisDataFailed,
    getJarvisDataSuccess,
    getRegisterInfoDataSuccess,
    getRegistrationSuccess,
    GET_JARVIS_DATA,
    GET_REGISTER_INFO_DATA,
    jarvisInfoSelector,
    selectedCountryCodeSelector,
    shippingAddressSelector,
    zipCodeSelector,
} from './../../../store/orderCreation';
import {
    userInfoSelector,
} from './../../../store/user';
import languageList from './../../../assets/language/en/lists.json';
import { Environment } from './../../../environments';

function* createCommercialUser(params: any): IterableIterator<any> {
    const body = params.payload || {};
    const { firstName, lastName, phoneNumber, microsoftAccount } = body;
    const postalCode = yield select(state => zipCodeSelector(state));
    const shippingAddress: any = yield select(state =>
        shippingAddressSelector(state)
    );
    const customerServiceLocationAddress: any = yield select(state =>
        customerServiceLocationAddressSelector(state)
    );
    const userInfo: any = yield select(state =>
        userInfoSelector(state)
    );

    const allCountries: any = yield select(state => countriesListSelector(state));
    const countryCode = (shippingAddress && shippingAddress.country && shippingAddress.country.toUpperCase().trim())
        || (customerServiceLocationAddress && customerServiceLocationAddress.country && customerServiceLocationAddress.country.toUpperCase().trim())
        || (userInfo && userInfo.countryCode && userInfo.countryCode.toUpperCase().trim()) // userInfo.countryCode will be ShipToCustomer countryCode
        || '';
    const userCountry = countryCode && Array.isArray(allCountries) && allCountries.find((country: any) => {
        // Shipping address has 3 letter iso code whereas userInfo has 2 letter country code
        return country
            && (
                (country.ISOCode && country.ISOCode.toUpperCase().trim() === countryCode)
                || (country.ISOCountryShortCode && country.ISOCountryShortCode.toUpperCase().trim() === countryCode)
            );
    });
    const userCountryNumber = userCountry && userCountry.ISOCountryNumber && userCountry.ISOCountryNumber.trim() || '840'; // Default to USA = 840
    const language: string = shippingAddress && shippingAddress.language
        || customerServiceLocationAddress && customerServiceLocationAddress.language
        || window.navigator.language
        // @ts-ignore - For some reason TS doesn't seem to recognize userLanguage, which is an fallback
        || window.navigator.userLanguage;
    const languageDetails = languageList
        && Array.isArray(languageList.languages)
        && languageList.languages.find((languageInfo) => languageInfo.code.toUpperCase() === language.toUpperCase());
    const languageNumber = languageDetails && languageDetails.lcid || '1033';
    const request: CreateCustomerUserRequest = {
        firstName,
        lastName,
        addresses: [{
            countryNumber: userCountryNumber,
            postalCode: postalCode || '',
        }],
        emails: [{ emailId: microsoftAccount }],
        contactNumbers: [{ number: phoneNumber }],
        preferenceInfo: {
            preferences: {
                languageId: languageNumber,
            },
        },
        // we are only creating users via ustrl and only creating enterprise users there
        category: 'enterprise',
    };

    const response: any = yield call(
        CustomerDal.createCustomerUser,
        request
    );
    return response;
}
export function* upsertDeviceRegistrationInfo(request: any): IterableIterator<any> {
    const body = request.payload || {};
    const { serialNumber, firstName, lastName, phoneNumber, microsoftAccount, orgName, isAccessoryRepairOrder } = body;

    try {
        yield put(actionStatusStarted({ actionName: GET_REGISTER_INFO_DATA }));

        // simply saves user's inputs into the store
        yield put(getRegisterInfoDataSuccess({ firstName, lastName, phoneNumber, microsoftAccount, orgName }));

        const deviceInfo: any = yield call(
            DeviceDal.getDevice,
            serialNumber
        );

        const existingRegistrationInfo = deviceInfo && deviceInfo.registration;
        let deviceRegistrationPayload = existingRegistrationInfo;

        let customerId = null;
        let jarvisOwnerId = null;
        const isCommercialRegistration: boolean = getUserTierConfig().commercialRegistration;
        const countryCode = yield select((state: any) => selectedCountryCodeSelector(state));

        //to get the current tier information and check whether Enterprise category or not
        const isEnterpriseCustomer = checkIsUserEnterpriseCategoryFromTier(getTierName());

        if (!existingRegistrationInfo) {
            // the device is an unregistered device.
            if (isCommercialRegistration || isEnterpriseCustomer) {
                // // ! data means no registration so need to create the user here
                customerId = yield call(createCommercialUser, request);
            } else if (Environment.getEnvironment.jarvisEmailCheck) { // this is consumer case in not registered flow
                // only check the email with Jarvis if the user is not in Commercial Tier (15)
                yield call(checkEmailValidity, { payload: { microsoftAccount } });

                const jarvisStatus: any = yield select((state: any) => actionStatusSelector(state)(GET_JARVIS_DATA));

                if (jarvisStatus.status === LoadingState.rejected) {
                    throw new Error('registration_call_failed_check_email');
                }
                const jarvis: any = yield select((state: any) => jarvisInfoSelector(state));
                jarvisOwnerId = jarvis && jarvis.id;
            }

            deviceRegistrationPayload = buildPayload({
                countryCode,
                customerId,
                deviceInfo,
                jarvisOwnerId,
                isAccessoryRepairOrder,
            });
        }
        else { // device IS already registered
            //TODO: Figure out how to get (existing) commercial user's customer id
            if (getUserTierConfig().commercialRegistration || isEnterpriseCustomer) {
                customerId = yield call(createCommercialUser, request);
            } else if (Environment.getEnvironment.jarvisEmailCheck) {
                // Find this consumer's customer id to OVERWRITE the registration
                yield call(checkEmailValidity, { payload: { microsoftAccount } });

                const jarvisStatus: any = yield select((state: any) => actionStatusSelector(state)(GET_JARVIS_DATA));

                if (jarvisStatus.status === LoadingState.rejected) {
                    throw new Error('registration_call_failed_check_email');
                }

                const jarvis: any = yield select((state: any) => jarvisInfoSelector(state));
                jarvisOwnerId = jarvis && jarvis.id;
            }
            deviceRegistrationPayload = buildPayload({
                countryCode,
                customerId,
                deviceInfo,
                jarvisOwnerId,
                isAccessoryRepairOrder,
            });
        }

        const updatedRegistrationInfo: any = yield call(
            RegisterDeviceDal.registerDevice,
            serialNumber,
            deviceRegistrationPayload
        );

        if (!updatedRegistrationInfo) {
            throw new Error('error_confirming_device_registration');
        }
        yield put(actionStatusSuccess({ actionName: GET_REGISTER_INFO_DATA }));
        yield put(getRegistrationSuccess(updatedRegistrationInfo));
    } catch (e) {
        yield put(actionStatusFailed({ actionName: GET_REGISTER_INFO_DATA, error: e }));
    }
}

export function* checkEmailValidity(request: any): IterableIterator<any> {
    const { microsoftAccount } = request.payload;

    yield put(actionStatusStarted({ actionName: GET_JARVIS_DATA }));

    try {
        const jarvis = yield call(RegisterDeviceDal.checkEmailValidity, { 'email_address': microsoftAccount });

        if (!jarvis) throw new Error('jarvis is missing');

        yield put(actionStatusSuccess({ actionName: GET_JARVIS_DATA }));
        yield put(getJarvisDataSuccess(jarvis));
    } catch (e) {
        yield put(getJarvisDataFailed());
        yield put(actionStatusFailed({
            actionName: GET_JARVIS_DATA,
            data: { microsoftAccount },
            error: e,
        }));
    }
}

function buildPayload({ countryCode, customerId, deviceInfo, jarvisOwnerId, isAccessoryRepairOrder }: any) {
    return {
        ...customerId && { customerId }, // for commercial, use customerId
        countryCode: countryCode || 'US',
        isActive: deviceInfo.status === 'Active',
        ...jarvisOwnerId && { jarvisOwnerId },
        serialNumber: deviceInfo.serialNumber,
        isAccessoryRepairOrder: isAccessoryRepairOrder || false,
    };
}